import * as THREE from 'three';
import { SCATTER, WORLD } from '../config/tuning';
import { Rng } from '../core/Rng';
import { BUILDING_ARCHETYPE_COUNT, createBuildingGeometry } from './generators/BuildingGen';
import { THEATRE } from './generators/KeraniTheatre';
import { createFoliageTexture, createTreeGeometry } from './generators/VegetationGen';
import type { Terrain } from './Terrain';

/**
 * Procedural scatter and settlement (SPEC §3.9.2).
 *
 * Placement is a *pure function of the seed and the terrain*: every candidate is tested against
 * slope, altitude and the waterline, so the forest grows where a forest would and the same
 * theatre produces the same forest on every machine. Nothing is authored by hand.
 *
 * Vegetation is placed once for the whole theatre and bucketed into a uniform grid at load. Each
 * frame only the buckets within range are walked and written into three instanced meshes, one per
 * LOD. Re-selecting from a grid is cheap; re-*generating* placements as the aircraft moves would
 * cost a terrain sample per candidate and show up as a hitch every few seconds.
 */

const DEG_TO_RAD = Math.PI / 180;

interface TreeInstance {
  x: number;
  y: number;
  z: number;
  rotationRad: number;
  scale: number;
}

const instanceMatrix = new THREE.Matrix4();
const instancePosition = new THREE.Vector3();
const instanceQuaternion = new THREE.Quaternion();
const instanceScale = new THREE.Vector3();
const rotationAxis = new THREE.Vector3(0, 1, 0);

export class Scatter {
  readonly object3d = new THREE.Group();

  /** Trees instanced last rebuild, by LOD. Reported in the debug overlay. */
  readonly lodCounts = [0, 0, 0];
  treeTotal = 0;
  buildingCount = 0;

  private readonly lodMeshes: THREE.InstancedMesh[] = [];
  private readonly buildingMeshes: THREE.InstancedMesh[] = [];
  private readonly foliageTexture: THREE.DataTexture;
  private readonly foliageMaterial: THREE.MeshLambertMaterial;
  private readonly buildingMaterial: THREE.MeshLambertMaterial;

  /** Trees bucketed by grid cell; the key packs the cell coordinates. */
  private readonly buckets = new Map<number, TreeInstance[]>();
  private readonly lastRebuildAt = new THREE.Vector3(Number.NaN, 0, 0);

  constructor(terrain: Terrain, seed: number = WORLD.scatterSeed) {
    this.foliageTexture = createFoliageTexture();
    this.foliageMaterial = new THREE.MeshLambertMaterial({
      map: this.foliageTexture,
      // Alpha *test*, not blend: no sorting, no separate pass, and it survives being drawn in
      // any order against the terrain.
      alphaTest: 0.5,
      transparent: false,
      side: THREE.DoubleSide,
      fog: true,
    });

    this.buildingMaterial = new THREE.MeshLambertMaterial({ fog: true, vertexColors: false });

    this.placeVegetation(terrain, seed);
    this.buildLodMeshes();
    this.buildSettlement(terrain, seed);
  }

  /**
   * Re-select instances for the camera's neighbourhood. Cheap enough to call every frame, but
   * gated on movement so a stationary camera costs nothing.
   */
  update(cameraPosition: THREE.Vector3): void {
    if (
      Number.isFinite(this.lastRebuildAt.x) &&
      this.lastRebuildAt.distanceTo(cameraPosition) < SCATTER.rebuildThresholdM
    ) {
      return;
    }
    this.lastRebuildAt.copy(cameraPosition);

    this.lodCounts[0] = 0;
    this.lodCounts[1] = 0;
    this.lodCounts[2] = 0;

    const radius = SCATTER.radiusM;
    const cellSize = SCATTER.cellSizeM;
    const cellRadius = Math.ceil(radius / cellSize);
    const centreX = Math.floor(cameraPosition.x / cellSize);
    const centreZ = Math.floor(cameraPosition.z / cellSize);

    const [near, far] = SCATTER.lodDistancesM;
    const radiusSquared = radius * radius;

    for (let cz = centreZ - cellRadius; cz <= centreZ + cellRadius; cz += 1) {
      for (let cx = centreX - cellRadius; cx <= centreX + cellRadius; cx += 1) {
        const bucket = this.buckets.get(packCell(cx, cz));
        if (bucket === undefined) continue;

        for (let i = 0; i < bucket.length; i += 1) {
          const tree = bucket[i];
          const dx = tree.x - cameraPosition.x;
          const dz = tree.z - cameraPosition.z;
          const distanceSquared = dx * dx + dz * dz;
          if (distanceSquared > radiusSquared) continue;

          const distance = Math.sqrt(distanceSquared);
          const lod = distance < near ? 0 : distance < far ? 1 : 2;
          const mesh = this.lodMeshes[lod];
          const index = this.lodCounts[lod];
          if (index >= SCATTER.maxInstancesPerLod) continue;

          instancePosition.set(tree.x, tree.y, tree.z);
          instanceQuaternion.setFromAxisAngle(rotationAxis, tree.rotationRad);
          instanceScale.setScalar(tree.scale);
          instanceMatrix.compose(instancePosition, instanceQuaternion, instanceScale);
          mesh.setMatrixAt(index, instanceMatrix);
          this.lodCounts[lod] = index + 1;
        }
      }
    }

    for (let lod = 0; lod < this.lodMeshes.length; lod += 1) {
      const mesh = this.lodMeshes[lod];
      mesh.count = this.lodCounts[lod];
      mesh.instanceMatrix.needsUpdate = true;
    }
  }

  dispose(): void {
    for (const mesh of [...this.lodMeshes, ...this.buildingMeshes]) {
      mesh.geometry.dispose();
      mesh.dispose();
    }
    this.foliageTexture.dispose();
    this.foliageMaterial.dispose();
    this.buildingMaterial.dispose();
  }

  /**
   * Walk the theatre once, testing every candidate against the placement rules and bucketing
   * the survivors. Runs at load; roughly a hundred thousand terrain samples.
   */
  private placeVegetation(terrain: Terrain, seed: number): void {
    const cellSize = SCATTER.cellSizeM;
    const half = WORLD.sizeM / 2;
    const cells = Math.floor(WORLD.sizeM / cellSize);
    const maxSlope = SCATTER.maxSlopeDeg * DEG_TO_RAD;

    for (let cz = 0; cz < cells; cz += 1) {
      for (let cx = 0; cx < cells; cx += 1) {
        const cellX = cx - cells / 2;
        const cellZ = cz - cells / 2;

        // Seeded per cell, so a cell's contents never depend on what was generated before it.
        const rng = new Rng(seed ^ Math.imul(cx + 1, 0x9e3779b1) ^ Math.imul(cz + 1, 0x85ebca6b));
        let bucket: TreeInstance[] | undefined;

        for (let i = 0; i < SCATTER.treesPerCell; i += 1) {
          const x = -half + (cx + rng.next()) * cellSize;
          const z = -half + (cz + rng.next()) * cellSize;
          const height = terrain.sampleHeight(x, z);

          if (height < SCATTER.minAltitudeM || height > SCATTER.maxAltitudeM) continue;
          if (terrain.sampleSlopeRad(x, z) > maxSlope) continue;

          // Thin out toward the tree line rather than stopping at a contour.
          const fade =
            1 - Math.max(0, height - (SCATTER.maxAltitudeM - SCATTER.altitudeFadeM)) / SCATTER.altitudeFadeM;
          if (rng.next() > fade) continue;

          // Keep the town clear; the settlement generator owns that ground.
          const townDistance = Math.hypot(x - THEATRE.townCentre[0], z - THEATRE.townCentre[1]);
          if (townDistance < THEATRE.townRadiusM) continue;

          if (bucket === undefined) {
            bucket = [];
            this.buckets.set(packCell(cellX, cellZ), bucket);
          }
          bucket.push({
            x,
            y: height,
            z,
            rotationRad: rng.next() * Math.PI * 2,
            scale: 1 + rng.range(-SCATTER.treeHeightVariation, SCATTER.treeHeightVariation),
          });
          this.treeTotal += 1;
        }
      }
    }
  }

  private buildLodMeshes(): void {
    for (let lod = 0; lod < 3; lod += 1) {
      const mesh = new THREE.InstancedMesh(
        createTreeGeometry(lod),
        this.foliageMaterial,
        SCATTER.maxInstancesPerLod,
      );
      mesh.count = 0;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      // Instances are placed across kilometres; the mesh's own bounds mean nothing to the culler.
      mesh.frustumCulled = false;
      this.lodMeshes.push(mesh);
      this.object3d.add(mesh);
    }
  }

  /**
   * The port town: buildings clustered on the flattened shelf the terrain generator left for
   * them, one instanced mesh per archetype so the whole settlement is six draw calls.
   */
  private buildSettlement(terrain: Terrain, seed: number): void {
    const rng = new Rng(seed ^ 0x70_57_70_57);
    const maxSlope = SCATTER.buildingMaxSlopeDeg * DEG_TO_RAD;
    const [townX, townZ] = THEATRE.townCentre;
    const spread = THEATRE.townRadiusM * SCATTER.buildingSpreadM;

    const perArchetype: THREE.Matrix4[][] = [];
    for (let i = 0; i < BUILDING_ARCHETYPE_COUNT; i += 1) perArchetype.push([]);

    for (let attempt = 0; attempt < SCATTER.townBuildings * 3; attempt += 1) {
      if (this.buildingCount >= SCATTER.townBuildings) break;

      // Denser toward the centre: sqrt keeps the distribution even, so squaring clusters it.
      const radius = spread * rng.next() ** 1.6;
      const angle = rng.next() * Math.PI * 2;
      const x = townX + Math.cos(angle) * radius;
      const z = townZ + Math.sin(angle) * radius;

      const height = terrain.sampleHeight(x, z);
      if (height < 2 || height > 120) continue;
      if (terrain.sampleSlopeRad(x, z) > maxSlope) continue;

      // Warehouses and sheds near the water, houses and blocks behind them.
      const archetype = height < 12 ? (rng.next() < 0.6 ? 3 : 5) : rng.int(0, 4);
      instancePosition.set(x, height - 0.4, z);
      instanceQuaternion.setFromAxisAngle(rotationAxis, Math.round(rng.next() * 8) * (Math.PI / 4));
      instanceScale.setScalar(1);
      perArchetype[archetype].push(
        new THREE.Matrix4().compose(instancePosition, instanceQuaternion, instanceScale),
      );
      this.buildingCount += 1;
    }

    const paletteRng = new Rng(seed ^ 0xc0_10c0);
    for (let archetype = 0; archetype < BUILDING_ARCHETYPE_COUNT; archetype += 1) {
      const matrices = perArchetype[archetype];
      if (matrices.length === 0) continue;

      const geometry = createBuildingGeometry(archetype, rng);
      const mesh = new THREE.InstancedMesh(geometry, this.buildingMaterial, matrices.length);
      for (let i = 0; i < matrices.length; i += 1) {
        mesh.setMatrixAt(i, matrices[i]);
        // Per-instance colour variation: a whitewashed coastal town, weathered unevenly.
        const shade = 0.62 + paletteRng.next() * 0.34;
        mesh.setColorAt(
          i,
          new THREE.Color(shade, shade * (0.94 + paletteRng.next() * 0.08), shade * 0.9),
        );
      }
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor !== null) mesh.instanceColor.needsUpdate = true;
      this.buildingMeshes.push(mesh);
      this.object3d.add(mesh);
    }
  }
}

/** Pack signed cell coordinates into one integer key. */
function packCell(cellX: number, cellZ: number): number {
  return ((cellX + 4096) << 13) | (cellZ + 4096);
}
